import { ApplePayCardNetwork } from '@ocx/graphql';
import {
  ApplePay,
  ApplePaySessionGetter,
  ApplePayCardNetwork as ApplePayPluginCardNetwork,
  ApplePayLineItem,
} from '@ocx/cap-apple-pay';

import { RequestDigitalWalletPaymentReturns } from './payment-provider-sdk-based.types';
import { applePayCardNetworkMap } from './payment-provider-sdk-based.constants';

export type PaymentProviderSDKBasedApplePayConstructorParams = {
  merchantId: string;
  merchantName: string | null;
  supportedNetworks: ApplePayCardNetwork[];
  getApplePaySession: ApplePaySessionGetter;
  isBillingZipCodeCollectionEnabled: boolean;
};

export class PaymentProviderSDKBasedApplePay {
  private readonly options: {
    merchantId: string;
    merchantName: string | null;
    supportedNetworks: ApplePayPluginCardNetwork[];
    isBillingZipCodeCollectionEnabled: boolean;
  };

  private readonly getApplePaySession: ApplePaySessionGetter;

  constructor(params: PaymentProviderSDKBasedApplePayConstructorParams) {
    this.options = {
      merchantId: params.merchantId,
      merchantName: params.merchantName,
      supportedNetworks: params.supportedNetworks.map((network) => applePayCardNetworkMap[network]),
      isBillingZipCodeCollectionEnabled: params.isBillingZipCodeCollectionEnabled,
    };
    this.getApplePaySession = params.getApplePaySession;
  }

  async getIsAvailable() {
    const isAvailable = await ApplePay.canMakeRequest({
      supportedNetworks: this.options.supportedNetworks,
      merchantId: this.options.merchantId,
    });
    return isAvailable;
  }

  async requestPayment(params: { lineItems: ApplePayLineItem[] }): Promise<RequestDigitalWalletPaymentReturns> {
    const result = await ApplePay.showPaymentRequest({
      requestInfo: {
        lineItems: params.lineItems,
        supportedNetworks: this.options.supportedNetworks,
        merchantName: this.options.merchantName || '',
        isBillingZipCodeCollectionEnabled: this.options.isBillingZipCodeCollectionEnabled,
      },
      configuration: {
        merchantId: this.options.merchantId,
        getApplePaySession: this.getApplePaySession,
      },
    });

    if (result.isCanceled) {
      return { token: '', nonce: null, canceled: true, paymentData: null };
    }

    const { transactionData, requestId, payment, billingPostalCode } = result;
    return {
      paymentData: JSON.stringify(payment),
      token: transactionData,
      nonce: null,
      canceled: false,
      billingZipCode: this.options.isBillingZipCodeCollectionEnabled ? billingPostalCode : null,
      completePayment: () => ApplePay.completePayment(requestId),
      cancelPayment: () => ApplePay.cancelPayment(requestId),
    };
  }
}
